import React, { useState } from "react"
import styles from "./outboundCampaign.module.css"
import {
    MdArrowForward,
    MdCheckCircle,
    MdContacts,
    MdHistory,
    MdOutlineCampaign,
    MdOutlineFileDownload,
    MdOutlineTableView,
} from "react-icons/md"
import { toast } from "react-toastify"
import Button from "../../components/Form/Button"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import ModalOutboundCampaign from "../../components/Modal/ModalOutboundCampaign"
import BackButton from "../../components/BackButton"
import { useAuth } from "../../contexts/authContext"

const OutboundCampaign = () => {
    const location = useLocation()
    const campaignBase = location.state?.campaignBase || null
    const [modalCampaign, setModalCampaign] = useState(Boolean(campaignBase))
    const [loading, setLoading] = useState(false)

    const { account_id } = useParams()
    const navigate = useNavigate()
    const { permissions } = useAuth()
    const canUpdateCampaignLimits = permissions?.settings >= 1

    const verifyNameIsValid = async name => {
        setLoading(true)
        try {
            await api.post("/outbound-campaigns/verify-name", { name })
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message)
            return false
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (dataConfig, message, dataFile) => {
        const rows = dataFile?.rows || []
        if (!rows.length) return toast.error("Nenhum dado válido para envio.")

        setLoading(true)
        try {
            const response = await api.post("/outbound-campaigns", { config: dataConfig, data: rows, message })
            toast.success(response?.data?.message)
            setModalCampaign(false)
            if (campaignBase) navigate(location.pathname, { replace: true, state: null })
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.topBar}>
                <BackButton fallback={`/${account_id}/integrations/chat`} />
                <Button type="button" variant="secondary" fullWidth={false} onClick={() => navigate(`/${account_id}/integrations/chat/outbound-campaign/list`)}>
                    <MdHistory /> Histórico de campanhas
                </Button>
            </div>

            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.eyebrow}>Campanhas WhatsApp</span>
                    <h1>Crie campanhas com um fluxo mais simples e seguro</h1>
                    <p>Configure público, responsáveis, mensagem e envio em etapas. Você pode substituir a planilha durante a criação sem recomeçar.</p>
                    <div className={styles.heroActions}>
                        <Button type="button" fullWidth={false} onClick={() => setModalCampaign(true)}>
                            <MdOutlineCampaign /> Criar campanha <MdArrowForward />
                        </Button>
                        <a href="https://oktto-model.s3.us-east-1.amazonaws.com/files/Modelo_Oktto_Campanha.xlsx" download>
                            <Button type="button" variant="secondary" fullWidth={false}><MdOutlineFileDownload /> Baixar modelo</Button>
                        </a>
                    </div>
                </div>
                <div className={styles.heroFlow} aria-label="Etapas da criação de campanha">
                    {["Campanha", "Origem", "Contatos", "Mensagem", "Revisão"].map((item, index) => (
                        <div key={item}><i>{index + 1}</i><span>{item}</span></div>
                    ))}
                </div>
            </section>

            {campaignBase && (
                <div className={styles.baseNotice}>
                    <MdOutlineCampaign />
                    <div><strong>Criando uma campanha semelhante</strong><span>As configurações da campanha anterior já estão preparadas. Selecione uma nova origem de contatos para continuar.</span></div>
                    <Button type="button" size="sm" fullWidth={false} onClick={() => setModalCampaign(true)}>Continuar</Button>
                </div>
            )}

            <section className={styles.sourcesSection}>
                <div className={styles.sectionHeading}>
                    <div><span className={styles.eyebrow}>Origem do público</span><h2>Como deseja selecionar os contatos?</h2></div>
                    <p>O restante da configuração é o mesmo, independentemente da origem.</p>
                </div>
                <div className={styles.sourceGrid}>
                    <article className={`${styles.sourceCard} ${styles.available}`}>
                        <span className={styles.sourceIcon}><MdOutlineTableView /></span>
                        <div><small>Disponível agora</small><h3>Importar uma planilha</h3><p>Use XLS, XLSX ou CSV. A planilha poderá ser substituída sem apagar as configurações já preenchidas.</p></div>
                        <ul>
                            <li><MdCheckCircle /> Até 10.000 registros</li>
                            <li><MdCheckCircle /> Validação de celulares e duplicados</li>
                            <li><MdCheckCircle /> Reconciliação automática de colunas</li>
                        </ul>
                        <Button type="button" fullWidth={false} onClick={() => setModalCampaign(true)}>Começar com planilha <MdArrowForward /></Button>
                    </article>
                    <article className={`${styles.sourceCard} ${styles.comingSoon}`}>
                        <span className={styles.sourceIcon}><MdContacts /></span>
                        <div><small>Em breve</small><h3>Usar contatos da Oktto</h3><p>Monte o público usando filtros, funis, responsáveis, etiquetas e dados já cadastrados no sistema.</p></div>
                        <ul>
                            <li><MdCheckCircle /> Sem exportar ou importar arquivos</li>
                            <li><MdCheckCircle /> Segmentação usando dados atualizados</li>
                            <li><MdCheckCircle /> Mesmo fluxo de mensagem e envio</li>
                        </ul>
                        <Button type="button" variant="secondary" fullWidth={false} disabled>Disponível em breve</Button>
                    </article>
                </div>
            </section>

            {modalCampaign && (
                <ModalOutboundCampaign
                    initialConfig={campaignBase || {}}
                    closeModal={() => setModalCampaign(false)}
                    verifyNameIsValid={verifyNameIsValid}
                    canUpdateCampaignLimits={canUpdateCampaignLimits}
                    onOpenLimitSettings={() => navigate(`/${account_id}/settings?section=campaign-limits`)}
                    onSubmit={handleSubmit}
                />
            )}
            {loading && <Loading fullPage />}
        </div>
    )
}

export default OutboundCampaign
